import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";

export type MemoryReviewSuggestion = {
  id: string;
  type: "person" | "organisation" | "idea" | "follow-up" | "opportunity";
  title: string;
  detail: string;
  evidence: string;
  status: "pending" | "approved" | "rejected";
};

export async function decideMemorySuggestion(
  userId: string,
  memoryId: string,
  suggestions: MemoryReviewSuggestion[],
  suggestion: MemoryReviewSuggestion,
  decision: "approved" | "rejected",
) {
  const memoryRef = doc(db, "users", userId, "memories", memoryId);
  const nextSuggestions = suggestions.map((item) => item.id === suggestion.id ? { ...item, status: decision } : item);
  const batch = writeBatch(db);
  if (decision === "approved") {
    const target = suggestionTarget(suggestion.type);
    const targetRef = doc(collection(db, "users", userId, target.collection));
    batch.set(targetRef, target.data(suggestion, memoryId));
  }
  batch.update(memoryRef, { reviewSuggestions: nextSuggestions, updatedAt: serverTimestamp() });
  await batch.commit();
}

export async function reviewAgentSuggestion(
  userId: string,
  suggestion: { id: string; title: string; detail: string },
  decision: "approve" | "reject",
  feedback = "",
) {
  await addDoc(collection(db, "users", userId, "agentSuggestionReviews"), {
    suggestionId: suggestion.id,
    decision,
    editedTitle: suggestion.title,
    editedDetail: suggestion.detail,
    feedback,
    status: "queued",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function decideIdentitySuggestion(userId: string, suggestionId: string, decision: "approved" | "rejected") {
  await updateDoc(doc(db, "users", userId, "identitySuggestions", suggestionId), {
    status: decision,
    reviewDecision: decision,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function decideOpportunity(userId: string, opportunityId: string, decision: "active" | "dismissed") {
  await updateDoc(doc(db, "users", userId, "opportunities", opportunityId), {
    status: decision,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

function suggestionTarget(type: MemoryReviewSuggestion["type"]) {
  if (type === "person") return {
    collection: "contacts",
    data: (item: MemoryReviewSuggestion, memoryId: string) => ({
      displayName: item.title,
      firstName: item.title,
      lastName: "",
      company: "",
      company2: "",
      companyOthers: "",
      title: "",
      title2: "",
      titleOthers: "",
      sources: ["memory"],
      notes: item.detail,
      sourceMemoryId: memoryId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  };
  if (type === "organisation") return {
    collection: "organisations",
    data: (item: MemoryReviewSuggestion, memoryId: string) => ({
      canonicalName: item.title,
      aliases: [item.title],
      description: item.detail,
      status: "approved",
      sourceMemoryIds: [memoryId],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  };
  if (type === "opportunity") return {
    collection: "opportunities",
    data: (item: MemoryReviewSuggestion, memoryId: string) => ({
      title: item.title,
      thesis: item.detail,
      stage: "Signal",
      status: "approved",
      sourceMemoryIds: [memoryId],
      nextAction: "Review and define next action",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  };
  return {
    collection: type === "follow-up" ? "followUps" : "ideas",
    data: (item: MemoryReviewSuggestion, memoryId: string) => ({
      title: item.title,
      detail: item.detail,
      status: "open",
      sourceMemoryIds: [memoryId],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }),
  };
}
